/**
 * Registry mapping canonical object types to elevation schematic
 * components. Each tier (v1 doors+windows, v2 appliances, v3 bath,
 * v4 cabinets+hoods, v5 long-tail) registers its components here so
 * `RenderWallElevation.js` never needs to know which tier owns
 * which type.
 *
 * A schematic component receives:
 *   { obj, widthMeters, heightMeters, strokeColor, strokeWidth, isCapture }
 * and returns the inner contents of the absolutely-positioned
 * <div> that wraps the object in the elevation view (a single
 * <svg> in practice). Stroke / selection / capture wiring stays in
 * the caller and is passed through as props.
 */

const REGISTRY = new Map();

/**
 * Register a schematic component for one or more canonical type
 * keys. Calling register() with a key that is already registered
 * overwrites the previous entry, which is intentional so a tier can
 * supply a more specific renderer that supersedes an earlier
 * fallback.
 */
export function registerSchematic(typeKey, Component) {
  if (Array.isArray(typeKey)) {
    typeKey.forEach((k) => REGISTRY.set(k, Component));
    return;
  }
  REGISTRY.set(typeKey, Component);
}

/**
 * Resolve a schematic component for an object. Returns null when no
 * registry entry matches; callers should then render the generic
 * rectangle fallback (or the registry's 'fallback' entry, if any).
 *
 * Resolution order:
 *   1. obj.type — the SOURCE OF TRUTH for what shape to draw. Every
 *      object that has one stores its canonical name here (range,
 *      refrigerator, lower-cabinet, …).
 *   2. obj.category keys — openings (door / window) carry no `type` at
 *      all and are identified by category alone, so this is the only
 *      way to draw them. It also catches anything else typeless.
 *
 * TYPE MUST WIN, and the reason is that `category` is a SET that keeps
 * whatever a scan called the object. A scanned vanity comes back from
 * RoomPlan as `storage`; the pipeline retypes it `type: 'lower-cabinet'`
 * and leaves the category alone (`vanity-assembly/placementUtils.js`
 * documents this), so the object reads
 * `{ type: 'lower-cabinet', category: { storage: true } }`. Reading
 * category first drew that vanity as a procedural DRESSER in every
 * elevation, PDF and panel thumbnail while the 3D scene — which resolves
 * `getFinishCategory(object) || object.type || …`, type first — drew it
 * as a cabinet with its door and drawer fronts. One object, two shapes.
 * 142 of the 1355 `lower-cabinet` objects on dev were in that state, and
 * the rate was flat across 16 months of scans, so it is what the pipeline
 * still produces rather than old data.
 *
 * It is the same defect `getObjectType` (objectDrawingUtils.js) already
 * fixed on its own side — its comment is the other half of this one:
 * a multi-category object was "falling through to whichever key happened
 * to land first in the deserialized map".
 *
 * Flipping the order moves exactly those 142 objects (128 `storage`,
 * 14 `table`, all → `lower-cabinet`) out of 17,856 that resolve to any
 * schematic at all; every other object in the corpus draws the same
 * component either way, because openings have no `type` to win with.
 */
export function getSchematicComponent(obj) {
  if (!obj) return null;
  if (obj.type) {
    const Component = REGISTRY.get(obj.type);
    if (Component) return Component;
  }
  if (obj.category && typeof obj.category === 'object') {
    for (const key of Object.keys(obj.category)) {
      const Component = REGISTRY.get(key);
      if (Component) return Component;
    }
  }
  return null;
}

/**
 * Test-only helper to inspect the registry contents.
 */
export function _getRegisteredTypes() {
  return Array.from(REGISTRY.keys());
}
